"use client";
import LoginForm from "./LoginForm";
import RegistrationForm from "./RegistrationForm";
import TpaMessage from "@/app/[locale]/auth/login/components/TPA/TpaMessage";
import {
  MFEContextResponse,
  PipelineUserDetails,
  TPALogistrationPage,
} from "@/lib/auth/getThirdPartyAuthProviders";
import React, { useEffect } from "react";
import { SwitchCallback } from "@/app/[locale]/auth/login/components/types";
import TpaGrid from "../TPA/TpaGrid";
import FormSwitchLink from "./FormSwitchLink";
import useLogistrationPageRedesign from "@/monarch/rules/logistrationPageRedesign/useLogistrationPageRedesign";
import { useAuth } from "@/components/AuthProvider";
import TpaGridSkeleton from "../TPA/TpaGridSkeleton";

interface MainFormsProps {
  isRegistration: boolean;
  switchToRegistration: SwitchCallback;
  switchToLogin: SwitchCallback;
  switchToPasswordRecovery: SwitchCallback;
  tpaProviders?: MFEContextResponse;
  isPasswordRecoveryComplete: boolean;
  isModal?: boolean;
  onClose?: () => void;
  updateLoggedInStatus?: () => void;
}

const MainForms: React.FC<MainFormsProps> = ({
  isRegistration,
  switchToRegistration,
  switchToLogin,
  switchToPasswordRecovery,
  tpaProviders,
  isPasswordRecoveryComplete,
  isModal = false,
  onClose,
  updateLoggedInStatus,
}) => {
  const { isLoadingTpa } = useAuth();
  const { data } = useLogistrationPageRedesign();
  const { showSsoFirst } = data || {};

  return (
    <div className="w-full md:max-w-[628px]">
      {/* Form */}
      <div className="px-6 md:p-0 w-full">
        <TpaMessage
          isRegistration={isRegistration}
          currentTpaProvider={tpaProviders?.contextData?.currentProvider}
        />
        {showSsoFirst &&
          (tpaProviders?.contextData?.currentProvider ||
          !tpaProviders ||
          isLoadingTpa ? (
            <TpaGridSkeleton />
          ) : (
            <div className="block md:hidden mb-6">
              <TpaGrid
                isRegistration={isRegistration}
                tpaProviders={tpaProviders}
                isLogistrationV1={true}
                showSsoFirst={showSsoFirst}
              />
            </div>
          ))}
        {isRegistration ? (
          <>
            {showSsoFirst && (
              <h3 className="text-gray-800 text-base mt-0 font-bold md:hidden">
                Or create a free account below:
              </h3>
            )}
            <RegistrationForm
              currentProvider={tpaProviders?.contextData?.currentProvider}
              finishAuthUrl={tpaProviders?.contextData?.finishAuthUrl}
              profileSuggestedDetails={
                tpaProviders?.contextData?.pipelineUserDetails ||
                ({} as PipelineUserDetails)
              }
              switchToLogin={switchToLogin}
              isModal={isModal}
              onClose={onClose}
              updateLoggedInStatus={updateLoggedInStatus}
            />
          </>
        ) : (
          <>
            {showSsoFirst && (
              <h3 className="text-gray-800 text-base mt-0 mb-4 font-bold md:hidden">
                Or log in with edX:
              </h3>
            )}
            <LoginForm
              currentProvider={tpaProviders?.contextData?.currentProvider}
              finishAuthUrl={tpaProviders?.contextData?.finishAuthUrl}
              switchToPasswordRecovery={switchToPasswordRecovery}
              passwordResetComplete={isPasswordRecoveryComplete}
              switchToRegistration={switchToRegistration}
              isModal={isModal}
              onClose={onClose}
              updateLoggedInStatus={updateLoggedInStatus}
            />
          </>
        )}

        {/* TPA Grid */}
        {tpaProviders?.contextData?.currentProvider ||
        !tpaProviders ||
        isLoadingTpa ? (
          <TpaGridSkeleton />
        ) : (
          <div className={showSsoFirst ? "hidden md:block" : ""}>
            <TpaGrid
              isRegistration={isRegistration}
              tpaProviders={tpaProviders}
              isLogistrationV1={true}
            />
          </div>
        )}
        {isModal && (
          <div className="mt-6">
            <FormSwitchLink
              prompt={
                isRegistration
                  ? "Already have an account?"
                  : "Don't have an account?"
              }
              linkText={isRegistration ? "Sign in" : "Register now"}
              onSwitch={isRegistration ? switchToLogin : switchToRegistration}
              isModal={isModal}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MainForms;
